import * as React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { Node } from "@xyflow/react";
import { BusinessService } from "../types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { mergeParams } from "../utils/url";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { MouseEventHandler } from "react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  MenuListProps: {
    disablePadding: true,
  },
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 320,
      padding: 0,
    },
  },
};

export const DependenciesPicker: React.FC<{
  id: string;
  serviceMapId: string;
  label: string;
  disableNew?: boolean;
  nodes: Node<BusinessService>[];
  value: string[];
  onChange: (v: string[]) => void;
  width?: number | string;
}> = ({
  id,
  label,
  disableNew = false,
  serviceMapId,
  nodes,
  value,
  onChange,
  width = 340,
}) => {
  const idToLabel = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nodes) {
      m.set(n.id, n.data.label);
    }
    return m;
  }, [nodes]);

  const handleChange = (event: SelectChangeEvent<typeof value>) => {
    const v = event.target.value;
    const value = (typeof v === "string" ? v.split(",") : v).filter((v) => !!v);
    onChange(value);
  };

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const onNew: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(
      `${pathname}?${mergeParams(searchParams, new URLSearchParams({ dialog: "new-system", serviceMapId })).toString()}`,
    );
  };

  return (
    <FormControl sx={{ m: 0, width }}>
      <FormLabel sx={{ mb: 0.75 }}>{label}</FormLabel>

      <Select
        id={`${id}-select`}
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput />}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((depId) => (
              <Chip
                key={depId}
                variant="filled"
                color="primary"
                label={idToLabel.get(depId) ?? depId}
                size="small"
                sx={{
                  color: "rgba(241, 245, 249, 0.95)",
                  fontWeight: 600,
                  backgroundColor: "rgba(6, 182, 212, 0.16)",
                  borderColor: "rgba(34, 211, 238, 0.38)",
                  "& .MuiChip-label": {
                    color: "inherit",
                  },
                }}
              />
            ))}
          </Box>
        )}
        MenuProps={MenuProps}
      >
        {!disableNew && (
          <MenuItem key="new-dep" onClick={onNew}>
            <ListItemText sx={{ width: "100%" }} onClick={onNew}>
              <Box display="flex" gap={1} alignItems="center">
                <FontAwesomeIcon icon={faPlus} />
                New
              </Box>
            </ListItemText>
          </MenuItem>
        )}
        {nodes.map((node) => (
          <MenuItem key={node.id} value={node.id}>
            <Checkbox checked={value.includes(node.id)} />
            <ListItemText primary={node.data.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
